/**
 * 权益商城相关接口类型定义
 * YAPI 分类: https://yapi.hellobike.cn/project/7647/interface/api/cat_61733
 */

import { MallEntranceTypeEnum } from '@/enums/rights';
import { ContractContentTypeEnum } from '@/api/rights/types';

/**
 * 分期商城协议场景枚举
 */
export enum CreditMallContractSceneEnum {
  /** 授信场景 */
  CREDIT = 'credit',
}

/**
 * 分期商城授信来源枚举
 */
export enum CreditMallFromEnum {
  /** 授信结果页 */
  FINANCE_SCREENING_AGREEMENT = 'finance_screening_agreement',
}

/**
 * 分期商城授信状态枚举
 */
export enum CreditMallStatusEnum {
  /** 初始化 */
  INIT = 1,
  /** 提交中 */
  SUBMITTING = 10,
  /** 审核中 */
  AUTHING = 20,
  /** 拒绝 */
  REJECT = 30,
  /** 成功 */
  SUCCESS = 40,
  /** 过期 */
  OVER_DUE = 50,
  /** 提交失败 */
  SUBMIT_FAIL = 60,
}

/** 整体转换后的映射关系 */
export enum InstallMallCreditStatusEnum {
  /** 初始化，可授信，待授信 */
  WAIT_CREDIT = 1,
  /** 授信提交中 */
  CREDIT_PROCESSING = 2,
  /** 授信成功 */
  CREDIT_SUCCESS = 3,
  /** 授信失败 */
  CREDIT_FAIL = 4,
}

/**
 * 资料审核页授信状态枚举
 * 40: 成功
 * 10: 授信中
 * 30: 授信失败
 */
export enum CreditMallReviewStatusEnum {
  /** 成功 */
  SUCCESS = 40,
  /** 授信中 */
  AUTHING = 10,
  /** 授信失败 */
  FAILED = 30,
}

/**
 * 协议样式枚举
 */
export enum AgreementStyleEnum {
  /** 方案1 - A 内嵌 */
  SCHEME_A = 'A',
  /** 方案2 - B 全屏 */
  SCHEME_B = 'B',
}

/**
 * 1. 首页入口概要信息查询接口
 * @yapi https://yapi.hellobike.cn/project/7647/interface/api/405663
 */
export type HomeSummaryParams = Record<string, never>;

export interface HomeSummaryResponse {
  /** 最高可授信额度 */
  applyAmount: string;
  /** 跳转地址 */
  jumpUrl: string;
  /** 存在有效期的授信申请 */
  hasEffective: boolean;
}

export interface ProductItemType {
  /** 商品ID */
  productId: string;
  /** 商品名称 */
  productName: string;
  /** 商品图片 */
  productIcon: string;
}

/**
 * 2. 授信失败页分期商城概要信息查询接口
 * @yapi https://yapi.hellobike.cn/project/7647/interface/api/405683
 */
export interface CreditFailedSummaryParams {
  /** 授信任务ID */
  creditTaskId: string;
}

export interface ContractInfo {
  /** 是否使用合同地址列表展示 true-使用合同列表展示 */
  isUseContractList: string;
  /** 合同列表信息 */
  contractList: ContractItem[];
}

export interface ContractItem {
  /** 合同名称 */
  name: string;
  /** 合同详情地址 */
  contentUrl: string;
  /** 内容类型 */
  contentType: ContractContentTypeEnum;
}

/**
 * 3. 使用入口信息查询接口
 * @yapi https://yapi.hellobike.cn/project/7647/interface/api/405735
 */
export type UseEntranceInfoParams = Record<string, never>;

export interface EntranceItem {
  /** 名称 */
  name: string;
  /** 描述 */
  desc: string;
  /** 入口类型 */
  entranceType: number;
}

export interface UseEntranceInfoResponse {
  /** 入口列表信息 */
  entranceList: EntranceItem[];
}

/**
 * 4. 获取入口跳转地址
 * @yapi https://yapi.hellobike.cn/project/7647/interface/api/405737
 */
export interface EntranceUrlParams {
  /** 入口类型 */
  entranceType: MallEntranceTypeEnum;
}

export interface EntranceUrlResponse {
  /** 跳转地址 */
  jumpUrl: string;
}

/**
 * 5. 发起授信申请接口
 * @yapi https://yapi.hellobike.cn/project/7647/interface/api/405761
 */
export interface CreditMallApplyParams {
  /** 来源 */
  from: string;
}


export interface CreditMallApplyResponse {
  /** 授信任务ID */
  creditTaskId?: string;
  /** 申请成功后跳转地址 */
  successJumpUrl?: string;
}

/**
 * 6. 获取授信结果接口
 * @yapi https://yapi.hellobike.cn/project/7020/interface/api/418945
 */
export interface QueryCreditApplyStatusParams {
  /** 授信任务id */
  creditTaskId?: string;
  from?: string;
  pollingProductId?: string[];
}

export interface QueryCreditApplyStatusResponse {
  /** 授信状态 */
  status: CreditMallStatusEnum;
  creditFrontStatus: InstallMallCreditStatusEnum;
  /** 跳转地址 */
  jumpUrl?: string;
  /** 授信金额 */
  creditAmount?: string;
  /** 商品ID列表 */
  productIdList?: string[];
}

/**
 * 7. 资料审核页展示接口
 * @yapi https://yapi.hellobike.cn/project/7020/interface/api/418935
 */

export interface QueryAgreementReviewShowResponse {
  /** 授信状态 40-成功 10-授信中 30-授信失败 */
  creditApplyStatus?: CreditMallReviewStatusEnum;
  creditFrontStatus: InstallMallCreditStatusEnum;
  /** 授信任务id */
  creditTaskId?: string;
  /** 是否展示分期商城协议模块 */
  showResult: boolean;
  /** 授信金额（授信成功返回） */
  applyAmount?: string;
  /** 跳转链接（授信成功返回） */
  jumpUrl?: string;
  showProductList: ProductItemType[];
}

/**
 * 8. 分期商城协议获取接口
 * @yapi https://yapi.hellobike.cn/project/7020/interface/api/418943
 */
export interface QueryContractListParams {
  /** 合同场景 */
  scene: CreditMallContractSceneEnum;
  /** 授信来源 */
  from: string;
  productId: string;
}

export interface ContractListItem {
  /** 合同名称 */
  contractName: string;
  /** 合同链接 */
  contractUrl: string;
  /** 合同类型 */
  contentType: ContractContentTypeEnum;
}

export interface QueryContractListResponse {
  /** 合同列表 */
  contractList: ContractListItem[];
  /** 授信来源 */
  contractFromList: string[];
}

/**
 * 9. 提现结果页分期商城概要信息查询接口
 * @yapi https://yapi.hellobike.cn/project/7647/interface/api/424568
 */
export interface WithdrawResultSummaryResponse {
  /** 授信任务ID */
  creditTaskId?: string;
  /** 可申请金额 */
  applyAmount: string;
  /** 授信状态 */
  creditStatus: CreditMallStatusEnum;
  /** 前端授信状态 */
  creditFrontStatus: InstallMallCreditStatusEnum;
  /** 协议样式：A方案1；B方案二 */
  agreementStyle: AgreementStyleEnum;
  /** 产品机构列表 */
  productIdList?: string[];
  /** 跳转链接 */
  jumpUrl?: string;
}

/**
 * 10. 查询合同列表接口（支持多产品）
 * @yapi https://yapi.hellobike.cn/project/7647/interface/api/424580
 * @description 查询合同列表:一次可以查询多个产品的合同列表,合同列表平铺返回
 */
export interface QueryMultiContractListParams {
  /** 产品id列表 */
  productIdList: string[];
  scene: CreditMallContractSceneEnum;
}

export interface QueryMultiContractListResponse {
  /** 合同列表 */
  contractList: ContractListItem[];
}
